import { attributes, Events, Collection, Model, define, AttributesMixin, type, Store } from "@type-r/models";
import { Linked, useCollection, useIO, useLink, useLinked, useModel } from "@type-r/react";
import React, { useMemo, useContext, useEffect } from "react";
import { ReadOnly, CollapsableSection, Flex, CompactTabs, FormCheckbox, SearchInput, SelectSortBy, TilesList, Tab, asCurrency, div, MultiSelectFilter, AddFilter, FiltersList, DateRangeFilter, Loading, TextFilter } from "lqx-react";
import { execMatchInvoices, execUnmatchInvoicesForMultiplePaymnets, Payment, RecsInvoice } from 'src/app/pages/inmatch/api';
import { InvalidDialog, InvoiceTile } from "./invoicesList";
import { InvoicesState, sortInvoicesOptions, MatchUnmatchButton, UnmatchButton } from "./potential-matches";
import { styled } from "@material-ui/styles";
import { globalStore } from "api";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { getDateOnly } from "lqx-react/src";

export const OpenInvoices = ({ getPayment, payment } : {
    getPayment: Function,
    payment : Payment,
}) => {    
    const state = useModel( OpenInvoicesState );
    const selectedInvoices = useCollection.subsetOf( state.invoices );
    const readOnly = useContext(ReadOnly);

    React.useEffect(() => {
        state.filter.initializeOptions();
    });

    React.useEffect(() => {
        selectedInvoices.reset();
    },
    [ state.filter['_changeToken'], state.category ])

    const $reload = useLink(false);
    const $selectedAll = useLink(false);
    const $isOpen = useLink(false);
    const $blockAction = useLink(false);
    
    const isready = useIO(async () => {
        state.page[state.category] = 1; 
        state.$.page.set(state.page);

        const params = buildSearchParams();        

        state.summaryCount = {...await RecsInvoice.getOpenInvoicesSummary(payment.id, params)}
        state.summaryCount.unmatched = state.summaryCount.all - state.summaryCount.matched;

        const invoices = await RecsInvoice.searchOpenInvoices(payment.id, params);

        state.invoices.set(invoices);
    },
    [ payment['_changeToken'], payment.matches['_changeToken'], $reload.value, state.filter.invoiceDueDate['_changeToken'], state.filter.filterAll, state.sortBy['_changeToken'],
        state.category, state.filter.invoiceNumber['_changeToken'], state.filter.additionalRefId['_changeToken'], state.filter.programId['_changeToken']]);


    React.useEffect(() => {
        if (state.filter.supplierName.isDone || (state.supplierIds.length && !state.filter.supplierName.selected)) {
            return $reload.set(!$reload.value);
        }

        if (state.filter.customerName.isDone || (state.customerIds.length && !state.filter.customerName.selected)) {
            return $reload.set(!$reload.value);
        }
    }, [state.filter['_changeToken']])

    const unmatchInvoices = async () => {
        let params;
        if ($selectedAll.value) {
            params = buildSearchParams();
        }

        const paymentInvoiceIds = selectedInvoices.filter(inv => !!inv.paymentMatchedId)
        .map(inv => ({ paymentId: inv.paymentMatchedId, invoiceId: inv.id }));
        await execUnmatchInvoicesForMultiplePaymnets(paymentInvoiceIds, params)
        .catch(e => {
            $blockAction.true();
        });
        selectedInvoices.reset();
    }

    const matchInvoices = async () => {
        let params;
        if ($selectedAll.value) {
            params = buildSearchParams();
        }

        const matches = await execMatchInvoices((selectedInvoices as any).map(inv => {
            return {
            appliedAmount: inv.unreconciledAmount,
            invoiceId: inv.id}
        }), payment.id, params)
        .catch(e => {
            $blockAction.true();

        });
        if (matches && matches.length) {
            Events.trigger( 'invoices:addMultiple', matches);
            selectedInvoices.reset();
        }
    }

    const [openInvoicesCount, matchedOpenInvoicesCount, unmatchedOpenInvoicesCount] = useMemo(() => {
        const matchedOpenInvoicesCount = state.invoices.filter(inv => !!inv.paymentMatchedId).length;
        const unmatchedOpenInvoicesCount = state.invoices.length - matchedOpenInvoicesCount;

        return [
            state.invoices.length,
            matchedOpenInvoicesCount,
            unmatchedOpenInvoicesCount
        ];
    }, [payment['_changeToken'], state.invoices['_changeToken']]);

    const loadMoreInvoices = async () => {
        state.page[state.category] = state.page[state.category] + 1; 
        state.$.page.set(state.page);

        const params = buildSearchParams();

        const invoices = await RecsInvoice.searchOpenInvoices(payment.id, params);

        state.invoices.set(state.invoices.models.concat(invoices));

        if ($selectedAll.value) {
            selectedInvoices.set(state.invoices.models);
        }
    };

    const isItemLoaded = (index) => {
        return index < categorized.length;
    };

    const categorized = useMemo( () => {
        const filtered = state.invoices && state.invoices
            .filter( x => (
                    state.category === "matched" ? !!x.paymentMatchedId :
                    state.category === "unmatched" ? !x.paymentMatchedId :
                    state.category === "all"
                )
            );
        return filtered; 
    },
    [ state.category, state.invoices['_changeToken'] ]);

    const buildSearchParams = () => {
        const params = new URLSearchParams({
            page: state.page[state.category].toString(),
            count: state.count.toString(),
            sortBy: state.sortBy.name,
            sortByAsc: state.sortBy.isAsc.toString(),
        });

        if (state.filter.supplierName && state.filter.supplierName.selected && state.filter.supplierName.selected.length) {
            state.supplierIds = globalStore.platformCompanies
                .filter(company => state.filter.supplierName.selected.includes(company.name))
                .map(company => company.id);
        }

        if (state.filter.customerName && state.filter.customerName.selected && state.filter.customerName.selected.length) {
            state.customerIds = globalStore.platformCompanies
                .filter(company => state.filter.customerName.selected.includes(company.name))
                .map(company => company.id);
        }

        if (state.supplierIds.length) {
            state.supplierIds.forEach(id => params.append("supplierIds", id.toString()))
        }
        if (state.customerIds.length) {
            state.customerIds.forEach(id => params.append("customerIds", id.toString()))
        }
        if (state.filter.invoiceDueDate.from && state.filter.invoiceDueDate.to) {
            params.append("invoiceDueDateFrom", getDateOnly(state.filter.invoiceDueDate.from));
            params.append("invoiceDueDateTo", getDateOnly(state.filter.invoiceDueDate.to));
        }

        if (state.filter.filterAll) {
            params.append("searchTerm", state.filter.filterAll);
        }

        if (state.category !== 'all') {
            params.append("matched", Boolean(state.category === 'matched').toString())
        }
        if (state.filter.invoiceNumber && state.filter.invoiceNumber.value) {
            params.append("invoiceNumber", state.filter.invoiceNumber.value);
        }
        if (state.filter.additionalRefId && state.filter.additionalRefId.value) {
            params.append("additionalRefId", state.filter.additionalRefId.value);
        }
        if (state.filter.programId && state.filter.programId.value) {
            params.append("programId", state.filter.programId.value);
        }

        return params;
    }

    useEffect(() => {
        if (selectedInvoices.length !== state.invoices.length) {
            $selectedAll.false();
        }
    }, [selectedInvoices['_changeToken']])
    
    return <CollapsableSection title={`Open Invoices`}
            right={ !$selectedAll.value ? 
                <TotalBlock row>
                    <MatchAmount>{asCurrency(selectedInvoices.reduce((a: number, b: RecsInvoice)=> +a + +b.unreconciledAmount ,0), payment.currency) + ` (${selectedInvoices.length})`}</MatchAmount>
                </TotalBlock> : undefined
            }
        >
        { readOnly ? null :
            <>
                <Flex row>
                    <CompactTabs $value={ state.$.category } >
                        <Tab value="unmatched">Unmatched ({unmatchedOpenInvoicesCount} out of {state.summaryCount.unmatched})</Tab>
                        <Tab value="matched">Matched ({matchedOpenInvoicesCount} out of {state.summaryCount.matched})</Tab>
                        <Tab value="all">All ({openInvoicesCount} out of ({state.summaryCount.all}))</Tab>
                    </CompactTabs>

                    <Flex grow />
                    
                    {Boolean(state.category !== 'all' && state.invoices.length) && (
                        Can(UserEntitlementCatalogs.BankEntryWrite) || Can(UserEntitlementCatalogs.BankEntryConfirm)) ? state.category === 'matched'
                        ?
                            <UnmatchButton
                                onClick={ unmatchInvoices }
                                disabled={
                                    !selectedInvoices || !selectedInvoices.length || selectedInvoices.some(inv => !inv.paymentMatchedId)
                                }
                                blockAction={selectedInvoices.some(inv => !!inv.remittanceAdviceIds.length)}
                                onClose={$blockAction.false}
                            />
                        :
                            <MatchUnmatchButton
                                onClick={ matchInvoices }
                                disabled={state.category === 'all' || !selectedInvoices || !selectedInvoices.length || selectedInvoices.some(inv => !!inv.paymentMatchedId)}
                                action={ 'Match' }
                                blockAction={$blockAction.value}
                                onClose={$blockAction.false}
                            />
                    : <></>}
                </Flex>
            </>
        }

        <FiltersList filter={state.filter} />
        <AddFilter
            filter={state.filter}
            filterAllPlaceholder="Filter by Invoice #, Invoice Due date, or Invoice amount or Additional Reference Identifier"
        /> 
        <Flex grow />
        
        <Flex row>
            {state.invoices.length && state.filter.anyFilterApplied && state.category === 'unmatched' ? (
                <FormCheckbox
                    disabled={readOnly || !state.invoices.length }
                    $checked={
                        Linked.value(
                            $selectedAll.value,
                            x => x ? (selectedInvoices.set( state.invoices.models ), $selectedAll.true()) : (selectedInvoices.reset(), $selectedAll.false())
                        )
                    }
                />) : null
            } 
            {state.invoices.length ? (
                <SelectSortBy 
                    $attr={ state.sortBy.$.name }
                    $asc={ state.sortBy.$.isAsc }
                    options={ sortInvoicesOptions }
                />) : null
            }
        </Flex>
        { isready ? 
            <TilesList
                items={ categorized }
                itemSize={ 42 }
                maxVisibleItemCount={ 15 }
                renderer={ InvoiceTile( getPayment, selectedInvoices, isItemLoaded )}
                selected={ undefined /* selected */ }
                itemCount={categorized.length + (categorized.length < state.summaryCount[state.category] ? 1 : 0)}
                isItemLoaded={isItemLoaded}
                loadMoreItems={loadMoreInvoices} 
            />: <Loading/>}

        
        { $isOpen.value ? 
            <InvalidDialog $isOpen={$isOpen} action={'matching'} /> : undefined }
    </CollapsableSection>;

};

export interface OpenInvoicesFilter extends AttributesMixin<typeof OpenInvoicesFilter> {}
@define
export class OpenInvoicesFilter extends Model {
    static attributes = {
        filterAll: '',
        invoiceDueDate: DateRangeFilter,
        supplierName: MultiSelectFilter,
        customerName: MultiSelectFilter,
        invoiceNumber: TextFilter,
        additionalRefId: TextFilter,
        programId: TextFilter,
    }

    initializeOptions() {
        var companies = globalStore.platformCompanies;

        const supplierNames = companies.map(cmp => cmp.name);
        this.supplierName.set({options: Array.from(new Set(supplierNames.filter(x => x)))});

        const customerNames = companies.map(cmp => cmp.name);
        this.customerName.set({options: Array.from(new Set(customerNames.filter(x => x)))});
    }

    get anyFilterApplied() {
        return this.filterAll || 
            (this.invoiceDueDate.from && this.invoiceDueDate.to) ||
            (this.supplierName.selected && this.supplierName.selected.length) ||
            (this.customerName.selected && this.customerName.selected.length) || 
            (this.invoiceNumber && this.invoiceNumber.value) ||
            (this.additionalRefId && this.additionalRefId.value) ||
            (this.programId && this.programId.value);
    }
}

interface OpenInvoicesState extends AttributesMixin<typeof OpenInvoicesState> { }
@define
class OpenInvoicesState extends Store {
    static attributes = {
        sortBy : {
            name : "unreconciledAmount",
            isAsc : true,
        },
        summaryCount: { all: 0, matched: 0, unmatched: 0 },
        invoices: [RecsInvoice],
        filter: type(OpenInvoicesFilter),
        category: type(String).value('unmatched'),
        page: { all: 1, matched: 1, unmatched: 1 },
        count: 100,
        itemCount: 0,
        supplierIds: [],
        customerIds: [],
    }
};

const TotalBlock = styled(Flex)({
    position: 'relative',
    textAlign: 'right',
    fontWeight: 500,
    marginRight: '310px',
    justifyContent: 'space-between',
    paddingBottom: '27px'
});

const MatchAmount = div({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    position: 'absolute'
})